import React, { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserCircleIcon, MapPinIcon } from '@heroicons/react/24/outline';
import api from '../../../middleware/api';
import API from '../../../routes/APIEndpoints';
import AddressList from '../../Common/Address/AddressList';
import UnifiedPhoneInput from '../../Common/Inputs/UnifiedPhoneInput/UnifiedPhoneInput';
import TherapistCreditForm from './TherapistCreditForm';

const TherapistForm = ({ therapist, onSuccess }) => {
    const queryClient = useQueryClient();
    const isCreating = !therapist;
    const [addresses, setAddresses] = useState(therapist?.addresses || []);
    const [phoneData, setPhoneData] = useState({
        phone: therapist?.phone || '',
        phonePrefix: therapist?.phonePrefix || '+33'
    });

    const mutation = useMutation({
        mutationFn: (data) => {
            if (isCreating) {
                return api.post(API.ADMIN_CREATE_THERAPIST, data);
            }
            return api.patch(`${API.ADMIN_UPDATE_THERAPIST}/${therapist.id}`, data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['therapists'] });
            if (onSuccess) onSuccess();
        }
    });

    useEffect(() => {
        setAddresses(therapist?.addresses || []);
    }, [therapist]);

    const handleSubmit = (e) => {
        e.preventDefault();
        const formData = new FormData(e.target);
        const data = {
            firstname: formData.get('firstname'),
            lastname: formData.get('lastname'),
            email: formData.get('email').toLowerCase().trim(),
            phone: phoneData.phone,
            phonePrefix: phoneData.phonePrefix,
            isVerified: formData.get('isVerified') ? true : false
        };
        mutation.mutate(data);
    };

    return (
        <div className="space-y-6">
            <form id="therapist-form" onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Informations personnelles */}
                <div className="space-y-4">
                    <div className="card bg-base-100 border border-base-200 p-5 shadow-sm space-y-4">
                        <h4 className="text-lg font-semibold flex items-center gap-2">
                            <UserCircleIcon className="h-5 w-5 text-primary" />
                            Informations personnelles
                        </h4>
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Nom</span>
                            </label>
                            <input
                                type="text"
                                name="lastname"
                                className="input input-bordered"
                                defaultValue={therapist?.lastname || ''}
                                required
                            />
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Prénom</span>
                            </label>
                            <input
                                type="text"
                                name="firstname"
                                className="input input-bordered"
                                defaultValue={therapist?.firstname || ''}
                                required
                            />
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Email</span>
                            </label>
                            <input
                                type="email"
                                name="email"
                                className="input input-bordered"
                                defaultValue={therapist?.email || ''}
                                required
                            />
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Téléphone</span>
                            </label>
                            <UnifiedPhoneInput
                                name="phone"
                                value={phoneData.phone}
                                phonePrefix={phoneData.phonePrefix}
                                onChange={(e) => setPhoneData(prev => ({
                                    ...prev,
                                    [e.target.name]: e.target.value
                                }))}
                                placeholder="Entrez le numéro de téléphone"
                            />
                        </div>

                        <div className="form-control flex items-center flex-row gap-2">
                            <input
                                type="checkbox"
                                id="isVerified"
                                name="isVerified"
                                className="checkbox checkbox-primary"
                                defaultChecked={therapist?.isVerified || false}
                            />
                            <label className="label cursor-pointer" htmlFor="isVerified">
                                <span className="label-text">Utilisateur vérifié</span>
                            </label>
                        </div>

                        <button
                            type="submit"
                            className="btn btn-primary w-full mt-4"
                            disabled={mutation.isPending}
                        >
                            {mutation.isPending ? 'Enregistrement...' : (isCreating ? 'Créer' : 'Mettre à jour')}
                        </button>
                    </div>
                </div>

                {/* Colonne de droite */}
                {!isCreating && (
                    <div className="space-y-6">

                        {/* Section des crédits */}
                        <TherapistCreditForm
                            therapist={therapist}
                        />

                        {/* Section des adresses */}
                        <div className="space-y-4">

                            <div className="card bg-base-100 border border-base-200 p-5 shadow-sm">
                                <AddressList
                                    userId={therapist.id}
                                    userType="therapist"
                                    addresses={addresses}
                                    setAddresses={setAddresses}
                                />
                            </div>
                        </div>
                    </div>
                )}
            </form>
        </div>
    );
};

export default TherapistForm; 